import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import {
  Layers, Search, Plus, ChevronRight, Trash2, Pencil, CheckCircle2, Package
} from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * Combos.jsx
 * - Lista combos (product_combos + products)
 * - KPI row + filtros + tabla estilo Productos.jsx
 * - Botón "Nuevo combo" (verde)
 * - Acciones: Editar / Archivar (soft-delete)
 *
 * Requisitos DB:
 * - product_combos
 * - product_combo_items
 */
const Combos = ({ onNavigate, sitioId: sitioActivo }) => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtros
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("activos");

  // confirm animation (estándar check verde)
  const [confirmPulse, setConfirmPulse] = useState({});
  const pulse = (key) => {
    setConfirmPulse((p) => ({ ...p, [key]: true }));
    window.setTimeout(() => {
      setConfirmPulse((p) => ({ ...p, [key]: false }));
    }, 650);
  };

  // performance
  const perfStartRef = useRef(null);

  const go = (module, entityId = null) => {
    try {
      if (typeof onNavigate === 'function') return onNavigate(module, entityId);
      if (entityId) return navigate(`/sistema/${module}/${entityId}`);
      return navigate(`/sistema/${module}`);
    } catch (e) {
      console.error('Error navegando:', e);
      toast.error('No se pudo navegar. Revisá Sistema.jsx / rutas.');
    }
  };

  const logUsage = async (button_clicked) => {
    try {
      if (!profile?.organization_id) return;
      await supabase.from('usage_logs').insert([{
        organization_id: profile.organization_id,
        user_id: profile?.id || null,
        user_role: profile?.role || null,
        module_id: 'combos',
        button_clicked,
        time_spent_seconds: 0
      }]);
    } catch (e) { /* telemetria best-effort */ }
  };

  const logPerformanceStart = () => {
    try {
      perfStartRef.current = Date.now();
    } catch (e) { /* telemetria best-effort */ }
  };

  const logPerformanceEnd = async (action = 'view_module') => {
    try {
      const start = perfStartRef.current;
      if (!profile?.organization_id || !start) return;
      const duration = Math.max(0, Math.round((Date.now() - start) / 1000));
      await supabase.from('performance_logs').insert([{
        user_id: profile?.id || null,
        organization_id: profile.organization_id,
        module_id: 'combos',
        duration_seconds: duration,
        action
      }]);
    } catch (e) { /* telemetria best-effort */ }
    finally {
      perfStartRef.current = null;
    }
  };

  const fmtMoney = (n) => {
    const v = Number(n || 0);
    return v.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      logPerformanceStart();

      const orgId = profile?.organization_id;
      if (!orgId) return;

      // Traigo combos + su producto + variantes (para consistencia con POS)
      const { data, error } = await supabase
        .from('product_combos')
        .select(`
          id,
          product_id,
          suggested_margin,
          suggested_price,
          final_price,
          is_active,
          archived_at,
          created_at,
          products:products (
            id,
            name,
            barcode,
            active,
            is_active,
            archived_at,
            product_type,
            category_id,
            product_variants (
              id, price, sku, barcode, attributes
            )
          )
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setRows(data || []);
      await logPerformanceEnd('load_module');
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron cargar los combos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.organization_id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const effectiveActive = (r) => {
    const comboOk = r?.is_active === true && !r?.archived_at;
    const prod = r?.products;
    const prodOk = (prod?.active === true) && !prod?.archived_at;
    const priceOk = Number(r?.final_price || 0) > 0;
    return comboOk && prodOk && priceOk;
  };

  const filtered = useMemo(() => {
    const b = busqueda.trim().toLowerCase();
    let list = Array.isArray(rows) ? rows : [];

    if (b) {
      list = list.filter(r => (r?.products?.name || '').toLowerCase().includes(b));
    }

    if (filtroEstado === 'activos') list = list.filter(effectiveActive);
    if (filtroEstado === 'inactivos') list = list.filter(r => !effectiveActive(r));
    return list;
  }, [rows, busqueda, filtroEstado]);

  const kpis = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const total = list.length;
    const activos = list.filter(effectiveActive).length;
    const sinPrecio = list.filter(r => Number(r?.final_price || 0) <= 0).length;
    const inactivos = total - activos;
    return { total, activos, inactivos, sinPrecio };
  }, [rows]);

  const archiveCombo = async (row) => {
    const orgId = profile?.organization_id;
    if (!orgId) return;

    const toastId = toast.loading('Archivando combo...');
    try {
      // Soft delete combo
      const { error: e1 } = await supabase
        .from('product_combos')
        .update({ is_active: false, archived_at: new Date().toISOString() })
        .eq('id', row.id)
        .eq('organization_id', orgId);

      if (e1) throw e1;

      // Soft delete product (coherencia catálogo)
      if (row?.product_id) {
        const { error: e2 } = await supabase
          .from('products')
          .update({ active: false, is_active: false, archived_at: new Date().toISOString() })
          .eq('id', row.product_id)
          .eq('organization_id', orgId);

        if (e2) throw e2;
      }

      toast.success('Combo archivado', { id: toastId });
      pulse(`archive-${row.id}`);
      logUsage('archivar_combo');
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo archivar', { id: toastId });
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[#F2F9FF] p-8">
        <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
          <Layers className="text-slate-300 animate-pulse" size={18} />
        </div>
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando Combos…</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-5 md:p-6 animate-nova-in">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md">
            <Layers size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Combos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Productos compuestos para vender en POS.
            </p>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => { logUsage('volver_productos'); go('productos'); }}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Volver a Productos"
          >
            <Package size={16} /> Productos
          </button>

          <button
            onClick={() => { logUsage('nuevo_combo'); go('combo-builder'); }}
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Plus size={16} /> Nuevo combo
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total</p>
            <Layers size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En catálogo</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Activos</p>
            <CheckCircle2 size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.activos}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Operables</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sin precio</p>
            <Plus size={16} className="text-slate-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${kpis.sinPrecio > 0 ? 'text-rose-700' : 'text-slate-800'}`}>
            {kpis.sinPrecio}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">A revisar</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Inactivos</p>
            <Trash2 size={16} className="text-slate-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${kpis.inactivos > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
            {kpis.inactivos}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">No operables</p>
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2.5">
          {/* BUSCADOR */}
          <div className="md:col-span-8">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Search size={12} /> Buscar
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                placeholder="BUSCAR COMBO POR NOMBRE..."
              />
            </div>
          </div>

          {/* ESTADO */}
          <div className="md:col-span-4">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Layers size={12} /> Estado
            </label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
              <option value="todos">Todos</option>
            </select>
          </div>
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Listado ({filtered.length})
            </span>
          </div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
            Gestión de combos
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-5 py-3 text-left">Combo</th>
                <th className="px-4 py-3 text-right">Margen</th>
                <th className="px-4 py-3 text-right">Sugerido</th>
                <th className="px-4 py-3 text-right">Final</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-14 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.28em]">
                    Sin resultados
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const name = r?.products?.name || 'Combo sin nombre';
                  const margin = Number(r?.suggested_margin || 0);
                  const sug = Number(r?.suggested_price || 0);
                  const fin = Number(r?.final_price || 0);
                  const active = effectiveActive(r);

                  return (
                    <tr
                      key={r.id}
                      className="hover:bg-blue-50/20 transition-all group"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                            <Layers size={16} className="text-slate-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[11px] font-black uppercase text-slate-800 truncate">
                              {name}
                            </div>
                            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                              ID: {String(r.id).slice(0, 8)}…
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                          {margin}%
                        </span>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                          {fmtMoney(sug)}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${fin > 0 ? 'text-slate-800' : 'text-rose-700'}`}>
                          {fin > 0 ? fmtMoney(fin) : 'SIN PRECIO'}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${
                          active
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                            : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}>
                          {active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { logUsage('editar_combo'); go('combo-builder', r.id); }}
                            className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all text-[9px] font-black uppercase tracking-widest flex items-center gap-2"
                            title="Editar"
                          >
                            <Pencil size={14} /> Editar
                          </button>

                          <button
                            onClick={() => archiveCombo(r)}
                            className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-rose-700 hover:bg-rose-50 transition-all text-[9px] font-black uppercase tracking-widest flex items-center gap-2 relative"
                            title="Archivar (soft delete)"
                          >
                            <Trash2 size={14} /> Archivar
                            {confirmPulse[`archive-${r.id}`] ? (
                              <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm nova-check-pop">
                                <CheckCircle2 size={14} className="text-emerald-600" />
                              </span>
                            ) : null}
                          </button>

                          <ChevronRight size={16} className="text-slate-200 group-hover:text-[#51A7FD] group-hover:translate-x-1 transition-all" />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        select {
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          background-size: 0.75rem;
        }
        .nova-check-pop { animation: novaCheckPop 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaCheckPop {
          0% { transform: scale(0.6); opacity: 0; }
          35% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default Combos;
